import { router, usePage } from "@inertiajs/react";
import { useMediaQuery } from "@uidotdev/usehooks";
import { format, startOfDay } from "date-fns";
import { MapPin } from "lucide-react";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { useForm } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useFormProvider } from "@/components/admin-portal/appointment/new-appointment-form";
import { useDateContext } from "@/components/providers/date-provider";
import { useStepper } from "@/components/shared/stepper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { getGenderIcon } from "@/hooks/use-gender";
import {
	type AppointmentBookingSchema,
	DEFAULT_VALUES_PACKAGE,
	DEFAULT_VALUES_THERAPIST,
	hasVisitTime,
	MITRA_ASSIGNMENT_REASON_ERRORS,
	mitraReasonVisitOf,
	toValidDate,
	visitsOf,
} from "@/lib/appointments/form";
import { findReasonLabel } from "@/lib/appointments/reason-label";
import {
	type BlockingStep,
	bookingErrorsByStep,
	stepPaths,
} from "@/lib/appointments/steps";
import { getBadgeVariantStatus } from "@/lib/appointments/utils";
import {
	toVoucherPreview,
	type VoucherPreview,
} from "@/lib/appointments/voucher-preview";
import {
	getFisiohomePartnerLabel,
	getPatientCondition,
	getPatientReferralLabel,
	IS_DEKSTOP_MEDIA_QUERY,
} from "@/lib/constants";
import { getCsrfToken } from "@/lib/csrf";
import {
	cn,
	formatCurrency,
	goBackHandler,
	populateQueryParams,
} from "@/lib/utils";
import type { AppointmentNewGlobalPageProps } from "@/pages/AdminPortal/Appointment/New";
import { DRAFT_STEPS, useAppointmentDraft } from "./use-appointment-draft";
import {
	usePreferredTherapistGender,
	useTherapistAvailability,
} from "./use-appointment-utils";

export const SESSION_STORAGE_FORM_KEY = "appointment-form";
export const SESSION_STORAGE_FORM_SELECTIONS_KEY =
	"appointment-form-selections";
// A pending one-time jump to the step a continued Draft was saved on, handed from
// New.tsx's onDraftLoaded to the stepper, which is the only place that can navigate.
const SESSION_STORAGE_RESTORE_STEP_KEY = "appointment-draft-restore-step";
// Which Draft this tab has already restored. Kept separately, and never cleared while
// the tab lives, because the request above is consumed the moment it is read.
const SESSION_STORAGE_RESTORED_DRAFT_KEY = "appointment-draft-restored";

/**
 * Ask the stepper to jump to `stepIndex` once, for `draftId`.
 *
 * Once per (tab, Draft), which is what the guard buys: loadDraft answers again after
 * every autosave and refresh, so an unguarded request would be re-armed indefinitely and
 * pull the admin back to the Draft's saved step long after they had moved on — including
 * straight after the admin used the blockers panel to jump to the step they have to fix,
 * which reads as that button simply not working.
 */
export const requestDraftStepRestore = (
	draftId: string | null,
	stepIndex: number,
) => {
	if (!draftId || stepIndex <= 0) return;

	try {
		if (
			window.sessionStorage.getItem(SESSION_STORAGE_RESTORED_DRAFT_KEY) ===
			draftId
		) {
			return;
		}

		window.sessionStorage.setItem(
			SESSION_STORAGE_RESTORE_STEP_KEY,
			String(stepIndex),
		);
	} catch {
		// Storage blocked or full: the form simply opens on step 1, which is safe.
	}
};

/**
 * Consume a pending jump for `draftId`, or null when there is none.
 *
 * Marks the Draft as restored before returning, so a later request for the same Draft is
 * refused rather than re-armed — the consumer remounts on every step change, so it cannot
 * remember this itself.
 */
export const takeDraftStepRestore = (draftId: string | null): number | null => {
	if (!draftId) return null;

	try {
		const pending = window.sessionStorage.getItem(
			SESSION_STORAGE_RESTORE_STEP_KEY,
		);
		if (!pending) return null;

		const stepIndex = Number.parseInt(pending, 10);
		window.sessionStorage.removeItem(SESSION_STORAGE_RESTORE_STEP_KEY);
		window.sessionStorage.setItem(SESSION_STORAGE_RESTORED_DRAFT_KEY, draftId);

		return Number.isNaN(stepIndex) || stepIndex <= 0 ? null : stepIndex;
	} catch {
		return null;
	}
};

// The scheduling schema stores an i18n key ("required" / "not_applicable") as the Mitra
// reason field's message, because that module is evaluated at import time and cannot
// call t(). Inline field errors translate it; a toast fallback shows the message
// verbatim, so without this it surfaces a bare "required". Every other field already
// carries human copy, which passes straight through. `t` must be bound to the
// "appointments-form" namespace.
export const humanizeSchedulingError = (
	message: string | undefined,
	t: (key: string) => string,
): string | undefined => {
	if (!message) return message;

	const isMitraKey = (
		Object.values(MITRA_ASSIGNMENT_REASON_ERRORS) as string[]
	).includes(message);

	return isMitraKey
		? t(`appt_schedule.fields.mitra_assignment_reason.errors.${message}`)
		: message;
};

// CONFIGURATION: Set this to true to use draft database, false to use session storage
export const USE_DRAFT_DATABASE = true;

export const useFinalStep = () => {
	const { props: globalProps } = usePage<AppointmentNewGlobalPageProps>();
	const { hasCompletedAllSteps } = useStepper();
	const [count, setCount] = useState(3);
	const redirectURL = useMemo(
		() => globalProps.adminPortal.router.adminPortal.appointment.index,
		[globalProps.adminPortal.router.adminPortal.appointment.index],
	);
	useEffect(() => {
		if (count > 0) {
			const timer = setTimeout(() => {
				setCount((prev) => prev - 1);
			}, 1000);
			return () => clearTimeout(timer);
		}

		if (count === 0) {
			setTimeout(() => {
				router.get(
					globalProps.adminPortal.router.adminPortal.appointment.index,
				);
			}, 1000);
		}
	}, [count, globalProps.adminPortal.router.adminPortal.appointment.index]);

	return { hasCompletedAllSteps, count, redirectURL };
};

export const useStepButtons = () => {
	const { isSuccessBooked } = useFormProvider();
	const isDekstop = useMediaQuery(IS_DEKSTOP_MEDIA_QUERY);
	const { t: taf } = useTranslation("appointments-form");
	const form = useFormContext<AppointmentBookingSchema>();
	const {
		prevStep,
		isLastStep,
		isOptionalStep,
		isDisabledStep,
		currentStep,
		isLoading,
		nextStep,
		setStep,
		steps,
	} = useStepper();

	// Draft management - only if configured to use draft database
	const {
		saveDraft,
		requestQueueCode,
		isLoading: isDraftSaving,
	} = useAppointmentDraft({
		onError: (error) => {
			console.error("Draft save error:", error);
		},
		onDraftSaved: USE_DRAFT_DATABASE
			? (draft) => {
					// Update URL with draftId
					const url = new URL(window.location.href);
					url.searchParams.set("draft_id", draft.id);
					window.history.replaceState({}, "", url.toString());
				}
			: undefined,
	});
	const [isSavingDraft, setIsSavingDraft] = useState(false);

	const isFirstStep = useMemo(
		() => currentStep?.label === taf("stepper.patient_profile.label"),
		[currentStep?.label, taf],
	);

	// Get current step name for draft
	const getCurrentStepName = useCallback(() => {
		if (currentStep?.label === taf("stepper.patient_profile.label")) {
			return DRAFT_STEPS[0];
		}
		if (currentStep?.label === taf("stepper.appt_settings.label")) {
			return DRAFT_STEPS[1];
		}
		if (currentStep?.label === taf("stepper.additional_settings.label")) {
			return DRAFT_STEPS[2];
		}
		if (currentStep?.label === taf("stepper.review.label")) {
			return DRAFT_STEPS[3];
		}
		return DRAFT_STEPS[0];
	}, [currentStep?.label, taf]);

	// Save draft when navigating to next step
	const saveDraftOnStep = useCallback(async () => {
		// Only save draft if configured to use draft database
		if (!USE_DRAFT_DATABASE) {
			return;
		}

		const values = form.getValues();
		const admins = values.additionalSettings?.admins || [];
		const primaryAdmin = admins[0]; // The first admin is the primary

		// Filter out admins with invalid IDs (0, undefined, null, or empty string)
		const validAdmins = admins.filter(
			(admin) =>
				admin?.id && admin.id !== 0 && admin.id !== "" && admin.id !== "0",
		);

		if (validAdmins.length === 0) {
			console.log("No valid admin selected, skipping draft save");
			// Show toast notification
			toast.error("Please select at least one admin before saving");
			return;
		}

		const validPrimaryAdmin =
			primaryAdmin?.id &&
			primaryAdmin.id !== 0 &&
			primaryAdmin.id !== "" &&
			primaryAdmin.id !== "0"
				? primaryAdmin
				: validAdmins[0];

		setIsSavingDraft(true);
		try {
			const values = form.getValues();

			const result = await saveDraft({
				adminPicId: validPrimaryAdmin?.id
					? String(validPrimaryAdmin.id)
					: undefined,
				adminIds: validAdmins.map((a) => String(a.id)),
				primaryAdminId: validPrimaryAdmin?.id
					? String(validPrimaryAdmin.id)
					: undefined,
				currentStep: getCurrentStepName(),
				formData: values,
				draftId: values.formOptions?.draftId
					? String(values.formOptions.draftId)
					: undefined,
			});

			if (result.success && result.draft) {
				// Update form with draft ID for future updates
				form.setValue("formOptions.draftId", String(result.draft.id));
			}
		} finally {
			setIsSavingDraft(false);
		}
	}, [form, saveDraft, getCurrentStepName]);

	const onPrevStep = useCallback(() => {
		prevStep();
	}, [prevStep]);
	const onBack = useCallback(() => {
		// Remove the appointment form data from session storage
		window.sessionStorage.removeItem(SESSION_STORAGE_FORM_KEY);

		goBackHandler();
	}, []);
	/**
	 * What currently blocks this booking, grouped by the step that renders it — the data
	 * behind the panel that sits above the step buttons.
	 *
	 * Shown rather than acted on. An earlier version toasted and jumped to the offending
	 * step by itself, which took the screen out from under the admin mid-thought; the
	 * panel names the step and offers a button instead, so moving there stays their call.
	 */
	const [blockingSteps, setBlockingSteps] = useState<BlockingStep[]>([]);
	const describeBlockers = useCallback(
		(errors: unknown, fallbackMessage?: string): BlockingStep[] => {
			const grouped = bookingErrorsByStep(errors).map(
				({ step, errors: stepErrors }) => ({
					step,
					label: steps[step]?.label ?? "",
					messages: [
						...new Map(
							stepErrors.map((error) => {
								const text =
									humanizeSchedulingError(error.message, taf) ?? error.message;
								// Every visit's reason error reads the same, so they collapse into one line.
								const visitIndex = mitraReasonVisitOf(error.path);
								return [text, { text, visitIndex }] as const;
							}),
						).values(),
					],
				}),
			);

			// A validation that fails without producing a single readable message would
			// otherwise render an empty panel, which reads as the button being broken again.
			if (grouped.length) return grouped;

			return [
				{
					step: -1,
					label: "",
					messages: [
						{
							text:
								fallbackMessage ||
								taf("submit_invalid", {
									defaultValue:
										"Some details are incomplete or invalid. Please review the earlier steps before submitting.",
								}),
						},
					],
				},
			];
		},
		[steps, taf],
	);

	// Blockers describe the attempt that produced them, so a step change clears them
	// rather than carrying a stale list onto a screen it no longer refers to.
	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the step, not on the setter
	useEffect(() => {
		setBlockingSteps([]);
	}, [currentStep?.label]);

	/**
	 * On Review there is no "Save & Next" to fail against, so the whole schema is checked
	 * on arrival — the admin sees what is missing before reaching for Submit rather than
	 * after pressing it. Nothing on that screen is editable, so once is enough.
	 *
	 * The ref is what makes "once" true. `describeBlockers` has to be a dependency for
	 * the linter, but it is rebuilt whenever react-i18next hands back a new `t` — and
	 * without the guard that would re-run a full-form `trigger()`, whose own state update
	 * renders again and can hand back another `t`. That is a render loop with a
	 * whole-schema validation inside it, on the one screen where the form is largest.
	 * Nothing here is editable, so re-running buys nothing anyway.
	 */
	const hasCheckedReview = useRef(false);
	useEffect(() => {
		if (!isLastStep) {
			// Rearm, so returning to Review after a fix checks again.
			hasCheckedReview.current = false;
			return;
		}
		if (hasCheckedReview.current) return;
		hasCheckedReview.current = true;

		let cancelled = false;
		form.trigger().then((isValid) => {
			if (cancelled) return;
			setBlockingSteps(isValid ? [] : describeBlockers(form.formState.errors));
		});

		return () => {
			cancelled = true;
		};
	}, [isLastStep, form, describeBlockers]);

	const onSubmit = async (options: { skipTherapist?: boolean } = {}) => {
		const { skipTherapist = false } = options;

		if (currentStep.label === taf("stepper.patient_profile.label")) {
			// Validation is per-namespace by default, but the UI no longer matches the
			// namespaces: the visit location moved to step 2 and the booking source
			// fields moved here from step 3. BOOKING_STEP_PATHS is the single table both
			// this split and the error-to-step lookup are derived from.
			if (!(await form.trigger(stepPaths(0)))) {
				setBlockingSteps(describeBlockers(form.formState.errors));
				return;
			}

			// request the queue code
			const formDataValues = form.getValues();
			if (!formDataValues?.formOptions?.queueCode) {
				const queueCodeResult = await requestQueueCode();

				if (!queueCodeResult.success) {
					const defaultQueueFailedMessage = "Failed to reserve queue code";
					const errorMessage =
						queueCodeResult.error || defaultQueueFailedMessage;
					console.error(`${defaultQueueFailedMessage}:`, errorMessage);
				}

				form.setValue(
					"formOptions.queueCode",
					queueCodeResult?.data?.queueCode,
				);
			}

			// Save draft before moving to next step
			await saveDraftOnStep();

			nextStep();
		}

		if (currentStep.label === taf("stepper.appt_settings.label")) {
			if (!(await form.trigger(stepPaths(1)))) {
				// Surface the failure: this step has display-only fields (e.g. the
				// series visit number) whose schema errors render no inline message,
				// which previously left the user on a silent dead-end.
				setBlockingSteps(
					describeBlockers(
						form.formState.errors,
						"Some appointment scheduling details are incomplete or invalid. Please review the visits before continuing.",
					),
				);
				return;
			}

			// show the alert dialog for remembering the user to skip therapist or not
			if (!skipTherapist && !watchTherapistValue?.id) {
				setIsOpenTherapistAlert(true);
				return;
			}

			// Save draft before moving to next step
			await saveDraftOnStep();

			nextStep();
		}

		if (currentStep.label === taf("stepper.additional_settings.label")) {
			// Only the fields this step renders — the rest of `additionalSettings` is
			// asked on step 1, and validating it from here refused to advance over a field
			// the admin could not see. Review checks the whole schema; that is its job.
			if (!(await form.trigger(stepPaths(2)))) {
				setBlockingSteps(describeBlockers(form.formState.errors));
				return;
			}

			// Save draft before moving to next step
			await saveDraftOnStep();

			nextStep();
		}
	};

	// * report a rejected submit the same way a rejected step is reported
	//
	// The last step's button is a plain `type="submit"`, so the rejection happens inside
	// react-hook-form's own handleSubmit, out of reach of onSubmit above. Watching
	// submitCount catches it here instead, where setStep is in scope — the alternative,
	// handling it at the <form> in New.tsx, is outside the stepper and cannot navigate.
	const { submitCount, errors: formErrors } = form.formState;
	const reportedSubmitCount = useRef<number | null>(null);
	useEffect(() => {
		// Adopt whatever count is already on the clock when this mounts, so a remount
		// (media-query swap, step change) does not re-toast an old rejection.
		if (reportedSubmitCount.current === null) {
			reportedSubmitCount.current = submitCount;
			return;
		}
		if (submitCount === reportedSubmitCount.current) return;

		reportedSubmitCount.current = submitCount;
		if (Object.keys(formErrors).length) {
			setBlockingSteps(describeBlockers(formErrors));
		}
	}, [submitCount, formErrors, describeBlockers]);

	// * watching the therapist value
	const [isOpenTherapistAlert, setIsOpenTherapistAlert] = useState(false);
	const watchTherapistValue = useWatch({
		control: form.control,
		name: "appointmentScheduling.therapist",
	});

	// * restore draft step when form is loaded from draft
	const watchDraftId = useWatch({
		control: form.control,
		name: "formOptions.draftId",
	});
	useEffect(() => {
		const stepIndex = takeDraftStepRestore(
			watchDraftId ? String(watchDraftId) : null,
		);
		if (stepIndex !== null) setStep(stepIndex);
	}, [watchDraftId, setStep]);

	// * redirect to the final step components
	useEffect(() => {
		if (isSuccessBooked) {
			setStep(steps?.length);
		}

		return () => {};
	}, [isSuccessBooked, steps, setStep]);

	return {
		isDekstop,
		isDisabledStep,
		isLoading: isLoading || isDraftSaving || isSavingDraft,
		isFirstStep,
		isLastStep,
		isOptionalStep,
		isOpenTherapistAlert,
		onSubmit,
		onPrevStep,
		onBack,
		setIsOpenTherapistAlert,
		blockingSteps,
		onGoToStep: setStep,
	};
};

// Payment expiry minutes → their label under payment_gateway.expiry_options.
const PAYMENT_EXPIRY_OPTION_KEYS: Record<string, string> = {
	"30": "minutes_30",
	"60": "hour_1",
	"180": "hours_3",
	"300": "hours_5",
	"720": "hours_12",
};

/** Visit status on Review: same reading and colours as the Visit cards on the scheduling step. */
function VisitStatusBadge({
	dateTime,
	therapistId,
}: {
	dateTime?: Date | string | null;
	therapistId?: string | number | null;
}) {
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.visit_status",
	});
	const status = !hasVisitTime(toValidDate(dateTime))
		? "unscheduled"
		: therapistId
			? "scheduled"
			: "pending_therapist_assignment";
	return (
		<Badge
			variant="outline"
			className={cn("uppercase", getBadgeVariantStatus(status))}
		>
			{t(
				status === "pending_therapist_assignment"
					? "pending_therapist"
					: status,
			)}
		</Badge>
	);
}

export const useReviewForm = () => {
	const { locale, tzDate, timeFormatDateFns } = useDateContext();
	const { props: globalProps } = usePage<AppointmentNewGlobalPageProps>();
	const errorsServerValidation = useMemo(
		() => (globalProps?.errors?.fullMessages as unknown as string[]) || null,
		[globalProps?.errors?.fullMessages],
	);
	const form = useFormContext<AppointmentBookingSchema>();
	const { setStep } = useStepper();
	const { i18n } = useTranslation();
	const { t } = useTranslation("appointments-form");
	const { t: tReview } = useTranslation("appointments-form", {
		keyPrefix: "review.price",
	});
	const review = useMemo(() => form.getValues(), [form.getValues]);
	const { selectedPackage, voucherCode, voucher, checkVoucher } =
		useBookingPrice();
	// Review is where the admin commits, so the price shown here is checked, not typed.
	useEffect(() => {
		if (voucherCode?.trim() && selectedPackage && voucher.status === "idle") {
			void checkVoucher();
		}
	}, [voucherCode, selectedPackage, voucher.status, checkVoucher]);
	const priceRows = useMemo(() => {
		const money = (value?: number | string | null) =>
			formatCurrency(value, { currency: selectedPackage?.currency || "IDR" });
		const packagePrice = selectedPackage?.totalPrice ?? null;
		const rows: { key: string; title: string; value: ReactNode }[] = [
			{
				key: "package-price",
				title: tReview("package"),
				value: money(packagePrice),
			},
		];
		if (voucherCode?.trim()) {
			rows.push({
				key: "voucher-discount",
				title: tReview("discount"),
				value:
					voucher.status === "valid" ? (
						`− ${money(voucher.discountAmount)}`
					) : voucher.status === "invalid" || voucher.status === "error" ? (
						<span className="text-destructive">{voucher.message}</span>
					) : (
						tReview("checking")
					),
			});
		}
		rows.push({
			key: "estimated-total",
			title: tReview("total"),
			value: (
				<span className="font-semibold">
					{money(
						voucher.status === "valid" ? voucher.finalPrice : packagePrice,
					)}
				</span>
			),
		});
		return rows;
	}, [selectedPackage, voucherCode, voucher, tReview]);
	// Resolved here rather than in the section list so the review never renders the
	// stored code: an unknown code degrades to the code itself, not to a blank row.
	// Gated on the flag rather than left to the empty catalogue the server then sends:
	// findReasonLabel falls back to the raw code when it cannot resolve one, so a Draft
	// that still carries an answer would put "CONDITION_BEYOND_KARPIS_SCOPE" on the
	// review screen. The value survives in form_data untouched — it is only not shown.
	const mitraAssignmentReasonLabel = useMemo(
		() =>
			globalProps?.adminPortal?.featureFlags?.mitraAssignmentReasonEnabled
				? findReasonLabel(
						globalProps?.mitraAssignmentReasons,
						visitsOf(review.appointmentScheduling).find(
							(visit) => visit.mitraAssignmentReason,
						)?.mitraAssignmentReason,
						i18n.language,
					)
				: null,
		[
			globalProps?.adminPortal?.featureFlags?.mitraAssignmentReasonEnabled,
			globalProps?.mitraAssignmentReasons,
			review.appointmentScheduling,
			i18n.language,
		],
	);

	const sections = useMemo(() => {
		const {
			contactInformation,
			patientDetails,
			appointmentScheduling,
			additionalSettings,
			formOptions,
		} = review;
		const onClickGMaps = (coordinate: number[]) => {
			window.open(
				`https://www.google.com/maps/search/?api=1&query=${coordinate.join(",")}`,
			);
		};

		return [
			{
				key: "order-settings",
				title: t("review.titles.order_settings"),
				stepValue: 0,
				subs: [
					{
						key: "",
						title: t("review.titles.draft_id"),
						value: formOptions?.draftId || "N/A",
					},
					{
						key: "",
						title: t("review.titles.order_request_code"),
						value: formOptions?.queueCode || "N/A",
					},
				],
			},
			{
				key: "contact",
				title: t("review.titles.contact"),
				stepValue: 0,
				subs: [
					{
						key: "contact-name",
						title: t("review.titles.name"),
						value: contactInformation.contactName,
					},
					{
						key: "contact-phone",
						title: t("review.titles.phone_number"),
						value: contactInformation.contactPhone,
					},
					{
						key: "contact-email",
						title: t("review.titles.email"),
						value: contactInformation?.email || "N/A",
					},
					{
						key: "contact-miitel-link",
						title: t("review.titles.miitel_link"),
						value: contactInformation?.miitelLink || "N/A",
					},
				],
			},
			{
				key: "patient-profile",
				title: t("review.titles.patient_profile"),
				stepValue: 0,
				subs: [
					{
						key: "patient-full-name",
						title: t("review.titles.full_name"),
						value: patientDetails.fullName,
					},
					{
						key: "patient-date-of-birth",
						title: t("review.titles.date_of_birth"),
						value: format(patientDetails.dateOfBirth, "PPP", { locale }),
					},
					{
						key: "patient-age",
						title: t("review.titles.age"),
						value: t("review.age_years", { count: Number(patientDetails.age) }),
					},
					{
						key: "patient-gender",
						title: t("review.titles.gender"),
						value: patientDetails.gender ? (
							<Badge variant="outline">
								<span className="flex items-center justify-end gap-1">
									{getGenderIcon(patientDetails.gender.toLowerCase())}
									{patientDetails.gender}
								</span>
							</Badge>
						) : (
							"N/A"
						),
					},
					{
						key: "patient-current-condition",
						title: t("review.titles.current_condition"),
						value: (
							<Badge variant="outline">
								{getPatientCondition(patientDetails?.condition)?.titleId ||
									patientDetails?.condition}
							</Badge>
						),
					},
					{
						key: "patient-complaint-description",
						title: t("review.titles.complaint_description"),
						value: patientDetails?.complaintDescription || "N/A",
					},
					{
						key: "patient-illness-onset-date",
						title: t("review.titles.illness_onset_date"),
						value: patientDetails?.illnessOnsetDate || "N/A",
					},
					{
						key: "patient-medical-history",
						title: t("review.titles.medical_history"),
						value: patientDetails?.medicalHistory || "N/A",
					},
				],
			},
			// Split out of Additionals: these three fields are asked on step 1 now, so
			// their edit button has to land there. Voucher and the payment gateway stay
			// behind in Additionals, which is still step 3.
			{
				key: "booking-source",
				title: t("review.titles.booking_source"),
				stepValue: 0,
				subs: [
					{
						key: "referral-source",
						title: t("review.titles.referral_source"),
						value: additionalSettings?.referralSource
							? [
									getPatientReferralLabel(additionalSettings.referralSource),
									additionalSettings?.customReferralSource,
								]
									.filter(Boolean)
									.join(" - ")
							: "N/A",
					},
					{
						key: "fisiohome-partner-booking",
						title: t("review.titles.fisiohome_partner_booking"),
						value: additionalSettings?.fisiohomePartnerName
							? [
									getFisiohomePartnerLabel(
										additionalSettings.fisiohomePartnerName,
									),
									additionalSettings?.customFisiohomePartnerName,
								]
									.filter(Boolean)
									.join(" - ")
							: "N/A",
					},
					{
						key: "notes",
						title: t("review.titles.notes"),
						value: additionalSettings?.notes || "N/A",
					},
				],
			},
			{
				key: "visit-location",
				title: t("review.titles.visit_location"),
				stepValue: 1,
				subs: [
					{
						key: "patient-region",
						title: t("review.titles.region"),
						value: patientDetails.location.city,
					},
					{
						key: "patient-postal-code",
						title: t("review.titles.postal_code"),
						value: patientDetails?.postalCode || "N/A",
					},
					{
						key: "patient-address",
						title: t("review.titles.address"),
						value: (
							<div className="space-y-2">
								<p>{patientDetails.address}</p>
								<Button
									type="button"
									variant="link"
									effect="hoverUnderline"
									iconPlacement="left"
									icon={MapPin}
									onClick={(event) => {
										event.preventDefault();
										onClickGMaps([
											patientDetails.latitude,
											patientDetails.longitude,
										]);
									}}
								>
									{t("review.view_on_map")}
								</Button>
							</div>
						),
					},
					{
						key: "patient-address-note",
						title: t("review.titles.address_note"),
						value: patientDetails?.addressNotes || "N/A",
					},
				],
			},
			{
				key: "bookings",
				title: t("review.titles.bookings"),
				stepValue: 1,
				subs: [
					{
						key: "service",
						title: t("review.titles.service"),
						value: appointmentScheduling.service.name.replaceAll("_", " "),
					},
					{
						key: "package",
						title: t("review.titles.package"),
						value: (
							<p>
								<span className="uppercase">
									{appointmentScheduling.package.name}
								</span>
								<span className="mx-1">&bull;</span>
								<span className="italic font-light">
									{t("appt_schedule.package_visits", {
										count: Number(appointmentScheduling.package.numberOfVisit),
									})}
								</span>
							</p>
						),
					},
					{
						key: "preferred-therapist-gender",
						title: t("review.titles.preferred_therapist_gender"),
						value: (
							<Badge variant="outline">
								<span className="flex items-center justify-end gap-1">
									{getGenderIcon(
										appointmentScheduling.preferredTherapistGender.toLowerCase(),
									)}
									{appointmentScheduling.preferredTherapistGender}
								</span>
							</Badge>
						),
					},
					{
						key: "visit-1",
						title: (
							<p className="font-bold">
								{t("appt_schedule.visit", { number: 1 })}
							</p>
						),
						value: (
							<VisitStatusBadge
								dateTime={appointmentScheduling.appointmentDateTime}
								therapistId={appointmentScheduling.therapist?.id}
							/>
						),
					},
					{
						key: "appointment-date-time-1",
						title: t("review.titles.appointment_date_time"),
						value: appointmentScheduling?.appointmentDateTime
							? format(
									appointmentScheduling.appointmentDateTime,
									`PPP, ${timeFormatDateFns}`,
									{ locale, in: tzDate },
								)
							: "N/A",
					},
					{
						key: "assigned-therapist-1",
						title: t("review.titles.assigned_therapist"),
						value:
							appointmentScheduling.therapist?.name?.toUpperCase() || "N/A",
					},
					// All visits display
					...(review.appointmentScheduling.seriesVisits?.flatMap((visit) => {
						const statusBadge = (
							<VisitStatusBadge
								key={`status-${visit.visitNumber}`}
								dateTime={visit.appointmentDateTime}
								therapistId={visit.therapist?.id}
							/>
						);

						return [
							{
								key: `visit-${visit.visitNumber}-header`,
								title: (
									<p className="font-bold">
										{t("appt_schedule.visit", { number: visit.visitNumber })}
									</p>
								),
								value: statusBadge,
							},
							{
								key: `visit-${visit.visitNumber}-datetime`,
								title: t("review.titles.appointment_date_time"),
								value: visit.appointmentDateTime
									? format(
											visit.appointmentDateTime,
											`PPP, ${timeFormatDateFns}`,
											{ locale, in: tzDate },
										)
									: t("review.not_scheduled"),
							},
							{
								key: `visit-${visit.visitNumber}-therapist`,
								title: t("review.titles.assigned_therapist"),
								value: visit.therapist?.name?.toUpperCase() || "N/A",
							},
						];
					}) || []),
					// Only for a Booking that actually has a Mitra Visit. A KARPIS-only
					// Booking shows no row at all rather than an empty placeholder — the
					// admin was never asked the question.
					...(mitraAssignmentReasonLabel
						? [
								{
									key: "mitra-assignment-reason",
									title: t("review.titles.mitra_assignment_reason"),
									value: mitraAssignmentReasonLabel,
								},
							]
						: []),
				],
			},
			{
				key: "additionals",
				title: t("review.titles.additionals"),
				stepValue: 2,
				subs: [
					...priceRows,
					{
						key: "voucher-code",
						title: t("review.titles.voucher_code"),
						value: additionalSettings?.voucherCode || "N/A",
					},
					{
						key: "payment-gateway",
						title: t("review.titles.payment_gateway"),
						value: !additionalSettings?.enablePaymentGateway
							? t("review.disabled")
							: PAYMENT_EXPIRY_OPTION_KEYS[
										additionalSettings?.paymentExpiryMinutes ?? ""
									]
								? `${t("review.enabled")} · ${t(`payment_gateway.expiry_options.${PAYMENT_EXPIRY_OPTION_KEYS[additionalSettings?.paymentExpiryMinutes ?? ""]}`)}`
								: t("review.enabled"),
					},
				],
			},
		];
	}, [
		review,
		locale,
		tzDate,
		timeFormatDateFns,
		mitraAssignmentReasonLabel,
		priceRows,
		t,
	]);
	// Plain number rather than the section list's stepValue union: the blocking-errors
	// panel jumps by step index too, and those come from BOOKING_STEP_PATHS, not from a
	// review section.
	const onEdit = useCallback((step: number) => setStep(step), [setStep]);

	return { sections, onEdit, errorsServerValidation };
};

export const useAdditionalSettingsForm = () => {
	const { props: globalProps } = usePage<AppointmentNewGlobalPageProps>();
	const form = useFormContext<AppointmentBookingSchema>();
	const watchAdditionalSettingsValue = useWatch({
		control: form.control,
		name: "additionalSettings",
	});
	const additionalFormOptions = useMemo(() => {
		const referralSources = globalProps.optionsData?.referralSources || [];
		const fisiohomePartnerNames =
			globalProps.optionsData?.fisiohomePartnerNames || [];

		return { referralSources, fisiohomePartnerNames };
	}, [
		globalProps.optionsData?.referralSources,
		globalProps.optionsData?.fisiohomePartnerNames,
	]);

	return {
		form,
		additionalFormOptions,
		watchAdditionalSettingsValue,
	};
};

/**
 * Loads the service list once a Region is known and the list is not on the page yet.
 * The Service field only loads it on focus, so a Draft resumed on a later step would
 * otherwise show packages without prices and price nothing on Review. Mounted once,
 * by the page, so the steps that read the list do not each fire the same request.
 */
export const useEnsureServiceList = (
	control: ReturnType<typeof useForm<AppointmentBookingSchema>>["control"],
) => {
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentNewGlobalPageProps>();
	const locationId = useWatch({ control, name: "patientDetails.location.id" });
	const draftId = useWatch({ control, name: "formOptions.draftId" });

	const servicesMissing = !globalProps.services && !!locationId;
	useEffect(() => {
		if (!servicesMissing) return;
		const { queryParams } = populateQueryParams(
			pageURL,
			deepTransformKeysToSnakeCase({
				locationId,
				draftId: draftId || undefined,
			}),
		);
		router.get(pageURL, queryParams, {
			only: ["services"],
			preserveScroll: true,
			preserveState: true,
			replace: true,
		});
	}, [servicesMissing, pageURL, locationId, draftId]);
};

/**
 * The money side of the Booking: the chosen package's price and what the typed voucher
 * does to it. The form stores only the package's id, so the price is read from the
 * service list (see useEnsureServiceList).
 */
export const useBookingPrice = () => {
	const { props: globalProps } = usePage<AppointmentNewGlobalPageProps>();
	const { t } = useTranslation("appointments-form");
	const form = useFormContext<AppointmentBookingSchema>();
	const packageValue = useWatch({
		control: form.control,
		name: "appointmentScheduling.package",
	});
	const voucherCode = useWatch({
		control: form.control,
		name: "additionalSettings.voucherCode",
	});

	const selectedPackage = useMemo(
		() =>
			globalProps.services
				?.flatMap((service) => service.packages ?? [])
				.find((pkg) => String(pkg.id) === String(packageValue?.id)),
		[globalProps.services, packageValue?.id],
	);

	const [voucher, setVoucher] = useState<VoucherPreview>({ status: "idle" });
	// Numbers each question; an answer whose number is no longer current is about a
	// code or package the admin has since changed, and is dropped.
	const latestCheck = useRef(0);
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset on change only
	useEffect(() => {
		latestCheck.current += 1;
		setVoucher({ status: "idle" });
	}, [voucherCode, packageValue?.id]);

	const checkVoucher = useCallback(async () => {
		const code = voucherCode?.trim();
		if (!code || !selectedPackage) return;
		const fallback = t("voucher.check_failed");
		const check = ++latestCheck.current;
		const answer = (preview: VoucherPreview) => {
			if (check === latestCheck.current) setVoucher(preview);
		};
		setVoucher({ status: "loading" });
		try {
			const response = await fetch("/api/v1/vouchers/preview", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": getCsrfToken(),
				},
				body: JSON.stringify({
					code,
					package_id: selectedPackage.id,
					original_price: Number(selectedPackage.totalPrice),
				}),
			});
			const body = await response.json().catch(() => null);
			answer(
				toVoucherPreview(response.status, body, fallback, {
					BONUS_RO_PACKAGE_INELIGIBLE: t("voucher.bonus_ro_package_ineligible"),
					BONUS_RO_NO_PACKAGE_DISCOUNT: t(
						"voucher.bonus_ro_no_package_discount",
					),
				}),
			);
		} catch {
			answer({ status: "error", message: fallback });
		}
	}, [voucherCode, selectedPackage, t]);

	return { selectedPackage, voucherCode, voucher, checkVoucher };
};

export const usePatientDetailsForm = () => {
	const form = useFormContext<AppointmentBookingSchema>();
	const watchPatientRecordSourceValue = useWatch({
		control: form.control,
		name: "formOptions.patientRecordSource",
	});
	const isExistingPatientSource = useMemo(
		() => watchPatientRecordSourceValue === "existing",
		[watchPatientRecordSourceValue],
	);

	return {
		isExistingPatientSource,
	};
};

export const useAppointmentSchedulingForm = () => {
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentNewGlobalPageProps>();
	const { tzDate } = useDateContext();
	const { t } = useTranslation("appointments-form");
	const form = useFormContext<AppointmentBookingSchema>();

	// Draft management for service/package changes
	const { saveDraft } = useAppointmentDraft({
		onError: (error) => {
			console.error("Draft save error:", error);
		},
	});
	const watchPatientDetailsValue = useWatch({
		control: form.control,
		name: "patientDetails",
	});
	const watchDraftIdValue = useWatch({
		control: form.control,
		name: "formOptions.draftId",
	});
	const watchAppointmentSchedulingValue = useWatch({
		control: form.control,
		name: "appointmentScheduling",
	});
	const watchServiceValue = useWatch({
		control: form.control,
		name: "appointmentScheduling.service.id",
	});
	const watchAppointmentDateTimeValue = useWatch({
		control: form.control,
		name: "appointmentScheduling.appointmentDateTime",
	});
	const watchPreferredTherapistGenderValue = useWatch({
		control: form.control,
		name: "appointmentScheduling.preferredTherapistGender",
	});
	const watchAllOfDayValue = useWatch({
		control: form.control,
		name: "formOptions.findTherapistsAllOfDay",
	});
	const [isLoading, setIsLoading] = useState({
		services: false,
		therapists: false,
	});

	// * for therapist availability and the isoline map
	const onChangeTherapistLoading = useCallback((value: boolean) => {
		setIsLoading((prev) => ({ ...prev, therapists: value }));
	}, []);
	const onSelectTherapist = useCallback(
		async (
			value: AppointmentBookingSchema["appointmentScheduling"]["therapist"],
		) => {
			form.setValue("appointmentScheduling.therapist", value, {
				shouldValidate: true,
			});

			// Auto-save draft when therapist is selected
			if (!USE_DRAFT_DATABASE) {
				return;
			}

			try {
				const values = form.getValues();
				const admins = values.additionalSettings?.admins || [];
				const validAdmins = admins.filter(
					(admin: any) => admin?.id && admin.id !== "0",
				);
				const primaryAdmin = validAdmins?.find((admin: any) => admin.isPrimary);
				const validPrimaryAdmin =
					primaryAdmin?.id && primaryAdmin.id !== "0"
						? primaryAdmin
						: validAdmins?.[0];

				if (validPrimaryAdmin) {
					const result = await saveDraft({
						adminPicId: String(validPrimaryAdmin.id),
						adminIds: validAdmins?.map((a: any) => String(a.id)),
						primaryAdminId: String(validPrimaryAdmin.id),
						currentStep: DRAFT_STEPS[1], // appointment_scheduling step
						formData: {
							...values,
							appointmentScheduling: {
								...values.appointmentScheduling,
								therapist: value,
							},
						},
						draftId: values.formOptions?.draftId
							? String(values.formOptions.draftId)
							: undefined,
					});

					// Update form with draft ID if a new draft was created
					if (result?.success && result?.draft?.id) {
						const currentDraftId = form.getValues("formOptions.draftId");
						if (!currentDraftId || currentDraftId !== String(result.draft.id)) {
							form.setValue("formOptions.draftId", String(result.draft.id));
						}
					}
				}
			} catch (error) {
				console.error(
					"Failed to auto-save draft on therapist selection:",
					error,
				);
			}
		},
		[form, saveDraft],
	);
	const onResetTherapistFormValue = useCallback(() => {
		// Use setValue instead of resetField for more reliable clearing
		form.setValue("appointmentScheduling.therapist", DEFAULT_VALUES_THERAPIST);
	}, [form]);
	const {
		onResetIsoline,
		onResetTherapistOptions,
		...therapistAndIsolineValues
	} = useTherapistAvailability({
		serviceIdValue: watchServiceValue,
		appointmentDateTImeValue: watchAppointmentDateTimeValue,
		preferredTherapistGenderValue: watchPreferredTherapistGenderValue,
		isAllOfDayValue: !!watchAllOfDayValue,
		patientValues: {
			fullName: watchPatientDetailsValue.fullName,
			address: watchPatientDetailsValue.address,
			latitude: watchPatientDetailsValue.latitude,
			longitude: watchPatientDetailsValue.longitude,
			locationId: watchPatientDetailsValue.location.id,
		},
		fetchURL: pageURL,
		onChangeTherapistLoading,
		onResetTherapistFormValue,
	});
	// reset the therapist availability field value, options values from server fetch data and isoline map
	const onResetAllTherapistState = useCallback(() => {
		onResetTherapistFormValue();
		onResetTherapistOptions();
		onResetIsoline();
	}, [onResetTherapistFormValue, onResetTherapistOptions, onResetIsoline]);
	const therapistAvailabilityHooks = {
		...therapistAndIsolineValues,
		onSelectTherapist,
		onResetAllTherapistState,
	};

	// * for preferred therapist gender field
	const prefGenderHooks = usePreferredTherapistGender({
		sourceOptions: globalProps.optionsData?.preferredTherapistGender,
	});

	// * for service field
	const servicesOption = useMemo(
		() => globalProps?.services,
		[globalProps?.services],
	);
	const [alertService, setAlertService] = useState<{
		title: string;
		description: string;
	} | null>(null);
	const onCheckServiceError = useCallback(() => {
		setAlertService(null);

		if (
			!watchAppointmentSchedulingValue?.service?.id ||
			!watchAppointmentSchedulingValue?.package?.id
		) {
			// * trigger the validation of the service and package (needs to select first)
			form.trigger("appointmentScheduling.service.name");
			form.trigger("appointmentScheduling.package.name");
			setAlertService({
				title: t("appt_schedule.service_required.title"),
				description: t("appt_schedule.service_required.description"),
			});
			return true;
		}

		return false;
	}, [
		watchAppointmentSchedulingValue?.service,
		watchAppointmentSchedulingValue?.package,
		t,
		form.trigger,
	]);
	const onFocusServiceField = useCallback(() => {
		const draftIdParam =
			watchDraftIdValue ||
			new URLSearchParams(window.location.search).get("draft_id");
		// fetch the services options data
		const { queryParams } = populateQueryParams(
			pageURL,
			deepTransformKeysToSnakeCase({
				locationId: watchPatientDetailsValue?.location?.id,
				draftId: draftIdParam || undefined,
			}),
		);
		router.get(pageURL, queryParams, {
			only: ["adminPortal", "flash", "errors", "services"],
			preserveScroll: true,
			preserveState: true,
			replace: false,
			onStart: () => {
				setIsLoading((prev) => ({
					...prev,
					services: true,
				}));
			},
			onFinish: () => {
				setTimeout(() => {
					setIsLoading((prev) => ({
						...prev,
						services: false,
					}));
				}, 250);
			},
		});
	}, [pageURL, watchPatientDetailsValue?.location?.id, watchDraftIdValue]);

	// * Helper function to save draft for service/package selection
	const saveDraftOnServicePackageChange = useCallback(async () => {
		// Only save draft if configured to use draft database
		if (!USE_DRAFT_DATABASE) {
			return;
		}

		const values = form.getValues();
		const admins = values.additionalSettings?.admins || [];
		const primaryAdmin = admins[0]; // The first admin is the primary

		// Filter out admins with invalid IDs (0, undefined, null, or empty string)
		const validAdmins = admins.filter(
			(admin) =>
				admin?.id && admin.id !== 0 && admin.id !== "" && admin.id !== "0",
		);

		if (validAdmins.length === 0) {
			// Don't show error for service/package changes, just skip draft save
			console.log(
				"No valid admin selected, skipping draft save on service/package change",
			);
			return;
		}

		const validPrimaryAdmin =
			primaryAdmin?.id &&
			primaryAdmin.id !== 0 &&
			primaryAdmin.id !== "" &&
			primaryAdmin.id !== "0"
				? primaryAdmin
				: validAdmins[0];

		try {
			const result = await saveDraft({
				adminPicId: validPrimaryAdmin?.id
					? String(validPrimaryAdmin.id)
					: undefined,
				adminIds: validAdmins.map((a) => String(a.id)),
				primaryAdminId: validPrimaryAdmin?.id
					? String(validPrimaryAdmin.id)
					: undefined,
				currentStep: DRAFT_STEPS[1], // appointment_scheduling step
				formData: values,
				draftId: values.formOptions?.draftId
					? String(values.formOptions.draftId)
					: undefined,
			});

			if (result.success && result.draft) {
				// Update form with draft ID for future updates
				form.setValue("formOptions.draftId", String(result.draft.id));

				// Return the updated values so caller can update formStorage if needed
				return form.getValues();
			}
		} catch (error) {
			// Don't show error for service/package changes, just log it
			console.error("Draft save error on service/package change:", error);
		}
	}, [form, saveDraft]);

	const onSelectService = useCallback(
		async (
			service: AppointmentBookingSchema["appointmentScheduling"]["service"],
		) => {
			// reset some values
			form.resetField("appointmentScheduling.package", {
				defaultValue: DEFAULT_VALUES_PACKAGE,
			});
			onResetAllTherapistState();

			// set the service selected value
			form.setValue("appointmentScheduling.service", service, {
				shouldValidate: true,
			});

			// Save draft after service selection and get updated values
			await saveDraftOnServicePackageChange();
		},
		[
			form.setValue,
			form.resetField,
			onResetAllTherapistState,
			saveDraftOnServicePackageChange,
		],
	);
	const serviceHooks = {
		servicesOption,
		alertService,
		onFocusServiceField,
		onSelectService,
		onCheckServiceError,
	};

	// * for package field
	const packagesOption = useMemo(
		() =>
			servicesOption
				?.filter(
					(service) =>
						String(service.id) === watchAppointmentSchedulingValue?.service?.id,
				)
				?.flatMap((service) => service.packages)
				?.filter((pkg) => pkg.active)
				?.sort((a, b) => a.numberOfVisit - b.numberOfVisit),
		[servicesOption, watchAppointmentSchedulingValue?.service?.id],
	);
	const onSelectPackage = useCallback(
		async (
			packageValue: AppointmentBookingSchema["appointmentScheduling"]["package"],
		) => {
			form.setValue("appointmentScheduling.package", packageValue, {
				shouldValidate: true,
			});
			onResetTherapistFormValue();

			// Save draft after package selection and get updated values
			await saveDraftOnServicePackageChange();
		},
		[form.setValue, onResetTherapistFormValue, saveDraftOnServicePackageChange],
	);
	const packageHooks = { packagesOption, onSelectPackage };

	// * appointment date time
	const watchSelectedTimeSlotValue = useMemo(() => {
		const validDateTime = toValidDate(watchAppointmentDateTimeValue);
		return validDateTime ? format(validDateTime, "HH:mm") : undefined;
	}, [watchAppointmentDateTimeValue]);
	const onSelectAllOfDay = useCallback(
		(value: boolean) => {
			const selectedDate = form.getValues(
				"appointmentScheduling.appointmentDateTime",
			);
			form.setValue("formOptions.findTherapistsAllOfDay", value);

			if (value) {
				form.setValue(
					"appointmentScheduling.appointmentDateTime",
					selectedDate ? startOfDay(selectedDate, { in: tzDate }) : null,
					{ shouldValidate: false },
				);
			} else {
				const now = new Date();
				const minutes = now.getMinutes();
				let hours = now.getHours();

				// Round up to next 30 minute interval
				let roundedMinutes = 0;
				if (minutes >= 30) {
					hours += 1;
				} else {
					roundedMinutes = 30;
				}

				// Create new date with selected date and rounded time
				const selectedDateObj = new Date(selectedDate || new Date());
				const nextHalfHour = new Date(
					selectedDateObj.getFullYear(),
					selectedDateObj.getMonth(),
					selectedDateObj.getDate(),
					hours,
					roundedMinutes,
					0, // seconds
					0, // milliseconds
				);

				form.setValue(
					"appointmentScheduling.appointmentDateTime",
					nextHalfHour,
					{ shouldValidate: false },
				);
			}
		},
		[form.setValue, form.getValues, tzDate],
	);
	const onSelectTimeSlot = useCallback(
		(value: string) => {
			const date = form.getValues("appointmentScheduling.appointmentDateTime");
			if (date) {
				const [hours, minutes] = value.split(":");
				const newDate = new Date(date);
				newDate.setHours(Number.parseInt(hours), Number.parseInt(minutes));
				form.setValue("appointmentScheduling.appointmentDateTime", newDate);
			}
		},
		[form.getValues, form.setValue],
	);

	// Auto-save draft when date/time changes
	const onDateTimeChange = useCallback(async () => {
		if (!USE_DRAFT_DATABASE) {
			return;
		}

		try {
			const values = form.getValues();
			const admins = values.additionalSettings?.admins || [];
			const validAdmins = admins.filter(
				(admin: any) => admin?.id && admin.id !== "0",
			);
			const primaryAdmin = validAdmins?.find((admin: any) => admin.isPrimary);
			const validPrimaryAdmin =
				primaryAdmin?.id && primaryAdmin.id !== "0"
					? primaryAdmin
					: validAdmins?.[0];

			if (validPrimaryAdmin) {
				const result = await saveDraft({
					adminPicId: String(validPrimaryAdmin.id),
					adminIds: validAdmins?.map((a: any) => String(a.id)),
					primaryAdminId: String(validPrimaryAdmin.id),
					currentStep: DRAFT_STEPS[1], // appointment_scheduling step
					formData: values,
					draftId: values.formOptions?.draftId
						? String(values.formOptions.draftId)
						: undefined,
				});

				// Update form with draft ID if a new draft was created
				if (result?.success && result?.draft?.id) {
					const currentDraftId = form.getValues("formOptions.draftId");
					if (!currentDraftId || currentDraftId !== String(result.draft.id)) {
						form.setValue("formOptions.draftId", String(result.draft.id));
					}
				}
			}
		} catch (error) {
			console.error("Failed to auto-save draft on date/time change:", error);
		}
	}, [form, saveDraft]);

	// Force rebuild - onDateTimeChange must be included
	return {
		...prefGenderHooks,
		...therapistAvailabilityHooks,
		...serviceHooks,
		...packageHooks,
		// Date/time hooks
		watchSelectedTimeSlotValue,
		onSelectAllOfDay,
		onSelectTimeSlot,
		onDateTimeChange, // Auto-save draft on date/time change
		// Form and watch values
		form,
		isLoading,
		watchAppointmentSchedulingValue,
		watchAllOfDayValue,
	};
};

/**
 * Custom hook to handle form reset when draft is loaded
 *
 * This hook ensures that:
 * - The form is reset with draft data only once when loaded
 * - Therapist fields are preserved from draft if they exist
 * - Series visits therapist fields are also properly preserved
 *
 * @param {UseFormResetProps} props - The hook properties
 */
export const useFormReset = ({
	form,
	formStorage,
	draftLoaded,
}: {
	form: ReturnType<typeof useForm<AppointmentBookingSchema>>;
	formStorage: AppointmentBookingSchema | null;
	draftLoaded: boolean;
}) => {
	// Ref to track if form has been reset for the current draft
	const hasResetForDraft = useRef(false);

	/**
	 * Effect to reset form when draft data is loaded
	 * - Only runs once per draft load
	 * - formStorage now includes therapist data (no longer filtered)
	 */
	useEffect(() => {
		// Check if we have form storage, draft is loaded, and we haven't reset yet
		if (formStorage && draftLoaded && !hasResetForDraft.current) {
			// Reset the form with the loaded data (therapist is now included)
			form.reset(formStorage);

			// Mark that we've reset for this draft
			hasResetForDraft.current = true;
		}
	}, [formStorage, form, draftLoaded]);

	/**
	 * Reset the flag when draftLoaded changes to false
	 * This allows the form to be reset again if a new draft is loaded
	 */
	useEffect(() => {
		if (!draftLoaded) {
			hasResetForDraft.current = false;
		}
	}, [draftLoaded]);
};
